import { useState, useEffect } from 'react';
import intl from 'react-intl-universal';

import localData from 'utils/localData';
import api from 'utils/api';

// components and parts
import { PageError, PageLoader } from 'components';
import { TABLE_TYPE } from 'components/PayrollTable/constants';
import {
  getDetailedTableData,
  getDetailedTableColumns,
} from './detailsColumns';
import { getKenshinTableData, getKenshinTableColumns } from './kenshinColumns';
import { getSummaryTableData, getSummaryTableColumns } from './summaryColumns';
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API, KENSHIN_API } from 'shared/constants/apis';
import { getFirstDateOfTheSameMonth } from 'utils/dateTime';
import { useSnackbar } from 'notistack';
import PayrollTable from 'components/PayrollTable';
import { useNavigate, Outlet } from 'react-router-dom';
import FeeAdjustment from 'components/PayrollTable/components/FeeAdjustment';
import { updatePaidWage, updatePaidWageKenshin } from '../shared/api';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
import logger from 'utils/logger';
import { isCurrentUserTaskManagerKenshin } from 'utils/privilege';

const log = logger.createLogger('DashboardPaidWage');

const DashboardPaidWage = () => {
  const { enqueueSnackbar } = useSnackbar();

  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDate = getFirstDateOfTheSameMonth(currentDate);

  const [loaded, setLoaded] = useState(false);
  const [loadedKenshin, setLoadedKenshin] = useState(false);
  const [ifError, setIfError] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.WAGES_START_DATE);
    return saved ? new Date(saved) : firstDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.WAGES_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });
  const [tableType, setTableType] = useState(TABLE_TYPE.SUMMARY);
  const [selectedDoctorId, setSelectedDoctorId] = useState(-1);
  const [selectedDoctorName, setSelectedDoctorName] = useState('');
  const [paidWageData, setPaidWageData] = useState([]);
  const [paidWageDataKenshin, setPaidWageDataKenshin] = useState([]);
  const [, setColumnOrder] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [updateApi, setUpdateApi] = useState(null);
  const navigate = useNavigate();
  const doesHaveKenshinPermission = isCurrentUserTaskManagerKenshin();
  const dstOrgId = localData.get(USER_DATA.CURRENT_ORGANIZATION_ID);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.WAGES_START_DATE,
      selectedStartDate.toISOString()
    );
  }, [selectedStartDate]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.WAGES_END_DATE,
      selectedEndDate.toISOString()
    );
  }, [selectedEndDate]);

  useEffect(() => {
    const apiVariables = {
      params: {
        organizationId: parseInt(dstOrgId),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    const kenshinApiVariables = {
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        username: localData.get(USER_DATA.USERNAME),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    api
      .get(DOLPHIN_API.PAID_WAGE, apiVariables)
      .then(async (data) => {
        const { paidWages, doctors } = data;
        const sortedPaidWages = paidWages.sort((wage1, wage2) =>
          wage1.id < wage2.id ? 1 : -1
        );
        setPaidWageData(sortedPaidWages);
        setDoctors(doctors);
        setLoaded(true);
      })
      .catch((error) => {
        log.error('error', error);
        setIfError(true);
      });

    api
      .get(KENSHIN_API.PAID_WAGE, kenshinApiVariables)
      .then(async (data) => {
        const { paidWages } = data;
        const sortedPaidWages = paidWages.sort((wage1, wage2) =>
          wage1.id < wage2.id ? 1 : -1
        );
        setPaidWageDataKenshin(sortedPaidWages);
        setLoadedKenshin(true);
      })
      .catch((error) => {
        log.error('error', error);
        setIfError(true);
      });
  }, [dstOrgId, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (loaded && loadedKenshin) {
      let columns = [];
      let data = [];
      if (tableType === TABLE_TYPE.SUMMARY) {
        columns = getSummaryTableColumns(
          setSelectedDoctorName,
          setSelectedDoctorId,
          setTableType,
          paidWageData,
          paidWageDataKenshin,
          doesHaveKenshinPermission
        );
        data = getSummaryTableData(paidWageData, paidWageDataKenshin);
        setUpdateApi(null);
      } else if (tableType === TABLE_TYPE.DETAILED) {
        columns = getDetailedTableColumns(openSeimitsuIssue);
        data = getDetailedTableData(paidWageData, selectedDoctorId);
        setUpdateApi(() => updatePaidWage);
      } else if (tableType === TABLE_TYPE.DETAILED_KENSHIN) {
        columns = getKenshinTableColumns(openKenshinIssue);
        data = getKenshinTableData(paidWageDataKenshin, selectedDoctorId);
        setUpdateApi(() => updatePaidWageKenshin);
      }
      setTableData(data);
      setColumns(columns);
      setColumnOrder(columns.map((column) => column.accessorKey));
    }
  }, [
    loaded,
    loadedKenshin,
    tableType,
    paidWageData,
    paidWageDataKenshin,
    selectedDoctorId,
  ]);

  if (!(loaded && loadedKenshin)) {
    return <PageLoader />;
  }
  if (ifError) {
    return <PageError />;
  }

  const refreshTableData = () => {
    if (selectedEndDate.toDateString() === new Date().toDateString()) {
      setSelectedEndDate(new Date());
    }
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const openSeimitsuIssue = (issueId) => {
    navigate(`/dashboard/paid-wage/seimitsu/issues/${issueId}`);
  };

  const openKenshinIssue = (issueId) => {
    navigate(`/dashboard/paid-wage/kenshin/issues/${issueId}`);
  };

  const FeeAdjustmentComponent = (table) => {
    return (
      <FeeAdjustment
        table={table}
        updateStatus={updateApi}
        refreshTableData={refreshTableData}
      />
    );
  };

  return (
    <div>
      <PayrollTable
        columns={columns}
        data={tableData}
        tableTitle={intl.get('paid_wage_text_paid_wage')}
        timeRangeStart={selectedStartDate}
        timeRangeEnd={selectedEndDate}
        updateTimeRangeStart={setSelectedStartDate}
        updateTimeRangeEnd={setSelectedEndDate}
        handleRefreshClick={refreshTableData}
        setTableType={setTableType}
        subtitle={selectedDoctorName}
        tableType={tableType}
        multiSelectComponent={FeeAdjustmentComponent}
      />
      <Outlet context={{ doctors, updateLocalProjectIssues: () => {} }} />
    </div>
  );
};

export default DashboardPaidWage;
